/**
 * Read adapter for analyst tables stored before the table plot (plots wave 6).
 *
 * A dense table built since wave 6 carries Python's heat-grid `plot` and is
 * drawn as is. A stored `analyst-table.v1` spec without one is read by its
 * shape with Python's rule (`table_plot.build_table_plot`): at least 4 rows
 * and 4 columns, every column scaled (a share over 100, a mean over its
 * stamped rating unit), as a heat grid with the stored heat read (legacy
 * reader item 7). The stored values are copied as they are; a suppressed
 * cell prints nothing; nothing is crowned. Any other table stays a table
 * (null).
 */

import { AnalystChartPlot, AnalystTableSpec } from '@digital-twins/shared-types'

import {
  DENSE_CUT_MIN_CUTS,
  DENSE_CUT_MIN_GROUPS,
  isDrawableAnalystPlot,
} from '@/utils/analystChartPlot'

import { AnalystLegacyPlotLabels, legacyHeatScale, storedRowBase } from './analystChartAdapters'

/** A stored dense table as a heat grid, or null when it stays a table. */
export function legacyAnalystTablePlot(
  table: AnalystTableSpec,
  labels: AnalystLegacyPlotLabels,
): AnalystChartPlot | null {
  if (table.rows.length < DENSE_CUT_MIN_GROUPS || table.columns.length < DENSE_CUT_MIN_CUTS) {
    return null
  }
  const scales = table.columns.map((column) => legacyHeatScale(column.unit, column.scaleUnit))
  if (scales.some((scale) => scale === null)) {
    return null
  }
  const columns = table.columns.map((column, index) => ({
    id: column.id,
    label: column.label,
    kind: 'measure',
    ...scales[index]?.column,
  }))
  const scaleByColumn = new Map(table.columns.map((column, index) => [column.id, scales[index]]))
  const rows = table.rows.map((row) => {
    const cellByColumn = new Map(row.cells.map((cell) => [cell.columnId, cell]))
    return {
      id: row.id,
      label: row.label,
      kind: 'segment',
      base: storedRowBase(row.cells.filter((cell) => !cell.isSuppressed).map((cell) => cell.baseN)),
      cells: table.columns.map((column) => {
        const cell = cellByColumn.get(column.id)
        const value = !cell || cell.isSuppressed || cell.value === null ? null : cell.value
        const scale = scaleByColumn.get(column.id)
        return {
          columnId: column.id,
          value,
          heat: value === null || !scale ? null : scale.heat(value),
        }
      }),
    }
  })
  if (!rows.some((row) => row.cells.some((cell) => cell.value !== null))) {
    return null
  }
  return {
    kind: 'heat-grid',
    data: {
      status: 'ok',
      columns,
      rows,
      labels: {
        unsupported_rating_scale: labels.unsupported_rating_scale,
        plot_low_base: labels.plot_low_base,
      },
    },
  }
}

/** What a featured table draws as a plot: Python's, else the stored read, else null (a table). */
export function analystTablePlot(
  table: AnalystTableSpec,
  labels: AnalystLegacyPlotLabels,
): { plot: AnalystChartPlot; isLegacy: boolean } | null {
  if (isDrawableAnalystPlot(table.plot)) {
    return { plot: table.plot, isLegacy: false }
  }
  const legacy = legacyAnalystTablePlot(table, labels)
  return legacy ? { plot: legacy, isLegacy: true } : null
}
